import React from "react";

export type ParamsType = {
    title?: string;
    onClose?: CallableFunction;
    confirmButtonLabel?: string | JSX.Element;
    cancelButtonLabel?: string | JSX.Element;
    extraConfirmButtonLabel?: string | JSX.Element;
};

export type AlertConfirmHookType = {
    isOpen: boolean;
    title?: string;
    message: string | JSX.Element;
    cancelButtonLabel: string | JSX.Element;
    extraConfirmButtonLabel?: string | JSX.Element;
    confirmButtonLabel: string | JSX.Element;
    close: () => void;
    confirm: () => void;
    extraConfirm: () => void;
    open: (
        message: string | JSX.Element,
        onConfirm: () => void,
        title?: string,
        onExtraConfirm?: () => void
    ) => void;
};

function useAlertConfirm({
    title,
    onClose,
    confirmButtonLabel = "CONFERMA",
    cancelButtonLabel = "CANCELLA",
    extraConfirmButtonLabel,
}: ParamsType): AlertConfirmHookType {
    const [isOpen, setIsOpen] = React.useState<boolean>(false);

    const messageRef = React.useRef<string | JSX.Element>("");
    const titleRef = React.useRef<string | undefined>(undefined);
    const onConfirmRef = React.useRef<null | CallableFunction>(null);
    const onExtraConfirmRef = React.useRef<null | CallableFunction>(null);

    const confirm = React.useCallback(() => {
        if (onConfirmRef.current) {
            onConfirmRef.current();
        }

        setIsOpen(false);
    }, []);

    const extraConfirm = React.useCallback(() => {
        if (onExtraConfirmRef.current) {
            onExtraConfirmRef.current();
        }

        setIsOpen(false);
    }, []);

    const close = React.useCallback(() => {
        if (onClose) {
            onClose();
        }

        setIsOpen(false);
    }, [onClose]);

    const open = React.useCallback(
        (
            message: string | JSX.Element,
            onConfirm: CallableFunction,
            tmpTitle?: string,
            onExtraConfirm?: CallableFunction
        ) => {
            messageRef.current = message;

            if (title) {
                titleRef.current = title;
            } else if (tmpTitle) {
                titleRef.current = tmpTitle;
            }

            onConfirmRef.current = onConfirm;

            if (onExtraConfirm) {
                onExtraConfirmRef.current = onExtraConfirm;
            }

            setIsOpen(true);
        },
        [title]
    );

    return {
        isOpen,
        title: titleRef.current,
        message: messageRef.current,
        cancelButtonLabel,
        confirmButtonLabel,
        extraConfirmButtonLabel,
        close,
        confirm,
        extraConfirm,
        open,
    };
}

export default useAlertConfirm;
